/**
 * Why a Zuzahlung could not be derived for an Abrechnungsfall (#1499).
 *
 * The derivation never guesses. A missing input yields no amount at all — it
 * yields one of these and the Fall is set aside, the same discipline
 * `PriceResolverService.resolveGkv` already runs under: *a substituted price is
 * worse than no price — a wrong amount is a wrong claim*. A guessed Zuzahlung is
 * worse still, because it is collected from a patient at the desk and then
 * deducted from what the Kasse is billed, so one wrong number is two wrong
 * numbers.
 *
 * Deliberately few. Every reason here names something a human can go and supply;
 * none of them is a state the software could resolve by trying harder.
 */
export var CopaymentSkipReason;
(function (CopaymentSkipReason) {
    /**
     * The patient record carries no `birthdate`.
     *
     * It is the **only** source for the § 61 SGB V under-18 case, and that case is
     * not an edge: four of the eight Zuzahlung-free Physio positions exist solely
     * because the service is deliverable to minors. Without a birthdate there is
     * no honest answer — assuming adult over-collects from a child, assuming minor
     * under-bills the Kasse.
     */
    CopaymentSkipReason["GEBURTSDATUM_FEHLT"] = "geburtsdatum fehlt";
    /**
     * A position the patient is liable for has no transcribed Zuzahlung on its
     * price line.
     *
     * Planned, not exceptional: only Physio § 125 is transcribed today, the other
     * six lineages land with TM-9557. *Not determinable* is the **correct** answer
     * there until they do — and the reason the column had to grow a third state in
     * #1495, since a zero would be indistinguishable from a contractual exemption.
     */
    CopaymentSkipReason["ZUZAHLUNG_NICHT_TRANSKRIBIERT"] = "zuzahlung nicht transkribiert";
    /**
     * The plan's Verordnung names no Heilmittelbereich (Feld 16).
     *
     * The Bereich decides whether the physician's `Zuzahlungsfrei` binds at all:
     * in Podologie the collection may only be suspended against a presented
     * Befreiungsbescheinigung, so the tick carries no authority there. Defaulting
     * the Bereich would silently pick a side of that inversion — and picking the
     * wrong side either collects from a patient who owes nothing or waives money
     * the Kasse expects to see deducted.
     *
     * ⚠ Answered from the Verordnung and from nowhere else since #1504. It used to
     * be read off whichever position hung on the plan, which derived the
     * Verordnung from its own consequences; a plan naming no Bereich is now this
     * reason rather than a quiet fallback to that reading.
     */
    CopaymentSkipReason["VERORDNUNG_OHNE_HEILMITTELBEREICH"] = "verordnung ohne heilmittelbereich";
    /**
     * A delivered unit's price could not be resolved at its Leistungstag.
     *
     * Raised by the caller assembling the derivation's inputs, not by the
     * derivation itself: the percentage follows the amount on the price line and
     * the flat 10 € is capped at `brutto − pct`, so neither half is answerable
     * without the price. Carries whatever `PricingResolutionError` said, so the
     * operator sees the pricing problem rather than a second one invented here.
     */
    CopaymentSkipReason["PREIS_NICHT_AUFLOESBAR"] = "preis nicht aufl\u00F6sbar";
})(CopaymentSkipReason || (CopaymentSkipReason = {}));
//# sourceMappingURL=copayment-skip-reason.enum.js.map